import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Heart, Gift, Coffee, AlertCircle, Sparkles, Rocket, Users, Star, Send } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import Layout from '../components/Layout'
import { useState } from 'react'

const Donate = () => {
  const [giftCardType, setGiftCardType] = useState("")
  const [giftCardCode, setGiftCardCode] = useState("")
  const [message, setMessage] = useState("")
  const [codeError, setCodeError] = useState("")
  const [isValidated, setIsValidated] = useState(false)

  // Validation patterns for different gift card types
  const codePatterns = {
    amazon: {
      // Amazon gift cards can be either 14 or 15 characters long (excluding dashes)
      // Only uppercase letters and numbers allowed
      pattern: /^[A-Z0-9]{4}-[A-Z0-9]{6}-(?:[A-Z0-9]{4}|[A-Z0-9]{5})$/,
      example: "XXXX-XXXXXX-XXXX(X)",
      description: "4 letters/numbers - 6 letters/numbers - 4 or 5 letters/numbers"
    },
    steam: {
      // Steam gift cards use 5-letter groups with only uppercase letters and numbers
      pattern: /^[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/,
      example: "XXXXX-XXXXX-XXXXX",
      description: "Three groups of 5 letters/numbers"
    },
    apple: {
      // Apple gift cards use 16 characters, only uppercase letters and numbers
      pattern: /^[A-Z0-9]{16}$/,
      example: "XXXXXXXXXXXXXXXX",
      description: "16 letters/numbers"
    },
    paysafe: {
      // Paysafe cards are exactly 16 digits
      pattern: /^[0-9]{16}$/,
      example: "0000000000000000",
      description: "16 numbers only"
    }
  }

  const validateGiftCard = (code: string, type: string) => {
    setCodeError("")
    
    if (!type) {
      setCodeError("Please select a gift card type first")
      return false
    }

    const pattern = codePatterns[type as keyof typeof codePatterns]
    if (!pattern) {
      setCodeError("Invalid gift card type")
      return false
    }

    // Remove any whitespace and dashes for initial validation
    const strippedCode = code.replace(/[-\s]/g, '').toUpperCase()

    if (!strippedCode) {
      setCodeError("Please enter a gift card code")
      return false
    }

    // For Amazon: Check length and valid characters
    if (type === 'amazon') {
      if (!/^[A-Z0-9]+$/.test(strippedCode)) {
        setCodeError("Amazon gift cards can only contain letters and numbers")
        return false
      }
      if (strippedCode.length !== 14 && strippedCode.length !== 15) {
        setCodeError("Amazon gift cards must be 14 or 15 characters long")
        return false
      }
    }

    // For Steam: Check length and valid characters
    if (type === 'steam') {
      if (!/^[A-Z0-9]+$/.test(strippedCode)) {
        setCodeError("Steam gift cards can only contain letters and numbers")
        return false
      }
      if (strippedCode.length !== 15) {
        setCodeError("Steam gift cards must be 15 characters long")
        return false
      }
    }

    // For Apple: Check length and valid characters
    if (type === 'apple') {
      if (!/^[A-Z0-9]+$/.test(strippedCode)) {
        setCodeError("Apple gift cards can only contain letters and numbers")
        return false
      }
      if (strippedCode.length !== 16) {
        setCodeError("Apple gift cards must be 16 characters long")
        return false
      }
    }

    // For Paysafe: Check length and valid characters
    if (type === 'paysafe') {
      if (!/^[0-9]+$/.test(strippedCode)) {
        setCodeError("Paysafe cards can only contain numbers")
        return false
      }
      if (strippedCode.length !== 16) {
        setCodeError("Paysafe cards must be 16 digits long")
        return false
      }
    }

    // If all basic validations pass, check if the formatted code matches the pattern
    const formattedCode = formatGiftCardCode(code, type)
    if (!pattern.pattern.test(formattedCode)) {
      setCodeError(`Invalid format. Expected: ${pattern.description}`)
      return false
    }

    setCodeError("")
    return true
  }

  const getCodeTemplate = (type: string, code: string): string => {
    const cleanCode = code.replace(/[-\s]/g, '').toUpperCase()
    
    switch (type) {
      case 'amazon':
        const template = 'XXXX-XXXXXX-' + (cleanCode.length > 14 ? 'XXXXX' : 'XXXX')
        return template.split('').map((char, i) => {
          if (char === '-') return '-'
          const pos = i - (i > 4 ? 1 : 0) - (i > 11 ? 1 : 0)
          return pos < cleanCode.length ? cleanCode[pos] : 'X'
        }).join('')
      case 'steam':
        return 'XXXXX-XXXXX-XXXXX'.split('').map((char, i) => {
          if (char === '-') return '-'
          const pos = i - (i > 5 ? 1 : 0) - (i > 11 ? 1 : 0)
          return pos < cleanCode.length ? cleanCode[pos] : 'X'
        }).join('')
      case 'apple':
        return cleanCode.padEnd(16, 'X').slice(0, 16)
      case 'paysafe':
        return cleanCode.padEnd(16, '0').slice(0, 16)
      default:
        return cleanCode
    }
  }

  const formatGiftCardCode = (code: string, type: string): string => {
    // Remove all spaces and dashes first
    let cleanCode = code.replace(/[-\s]/g, '').toUpperCase()

    switch (type) {
      case 'amazon':
        // Format as XXXX-XXXXXX-XXXX(X)
        if (cleanCode.length <= 4) {
          return cleanCode
        } else if (cleanCode.length <= 10) {
          return `${cleanCode.slice(0, 4)}-${cleanCode.slice(4)}`
        } else {
          return `${cleanCode.slice(0, 4)}-${cleanCode.slice(4, 10)}-${cleanCode.slice(10, 15)}`
        }
      case 'steam':
        // Format as XXXXX-XXXXX-XXXXX
        if (cleanCode.length <= 5) {
          return cleanCode
        } else if (cleanCode.length <= 10) {
          return `${cleanCode.slice(0, 5)}-${cleanCode.slice(5)}`
        } else {
          return `${cleanCode.slice(0, 5)}-${cleanCode.slice(5, 10)}-${cleanCode.slice(10, 15)}`
        }
      case 'apple':
        // No formatting, just 16 characters
        return cleanCode.slice(0, 16)
      case 'paysafe':
        // No formatting, just 16 digits
        return cleanCode.slice(0, 16)
      default:
        return cleanCode
    }
  }

  const handleGiftCardCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = formatGiftCardCode(e.target.value, giftCardType)
    setGiftCardCode(newCode)
    validateGiftCard(newCode, giftCardType)
  }

  const handleSubmitGiftCard = () => {
    if (!validateGiftCard(giftCardCode, giftCardType)) return
    const selectedCard = giftCards.find(card => card.value === giftCardType)
    const cardName = selectedCard ? selectedCard.name : giftCardType
    
    const emailTemplate = `
Dear Fembot Team,

I would like to support your project with a gift card donation:

🎁 Gift Card Details
───────────────────
Type: ${cardName}
Code: ${giftCardCode}

${message ? `
${message}
` : ''}

Thank you for making Discord better!
`
    const subject = encodeURIComponent(`Supporting Fembot with ${cardName}`)
    const body = encodeURIComponent(emailTemplate)
    window.location.href = `mailto:Fembot-Team<lunaxxdev@gmail.com>?subject=${subject}&body=${body}`
  }

  const giftCards = [
    { 
      name: "Amazon.de Gift Card",
      value: "amazon",
      description: "Redeemable on Amazon Germany (amazon.de)"
    },
    { 
      name: "Steam Gift Card",
      value: "steam",
      description: "For Steam Store purchases in EUR currency"
    },
    { 
      name: "Apple",
      value: "apple",
      description: "German Apple Gift Cards for App Store"
    },
    { 
      name: "PaySafeCard",
      value: "paysafe",
      description: "Available at many retailers across Germany"
    }
  ]

  return (
    <Layout>
      <div className="py-24 bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800">
        <div className="py-24 bg-gradient-to-b from-white to-white dark:from-gray-900 dark:to-gray-875">
          <div className="relative container mx-auto px-4">
            <div className="text-center space-y-6 max-w-3xl mx-auto">
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 rounded-full px-4 py-1 text-sm leading-6 text-fembot-pink ring-1 ring-fembot-pink/10 hover:ring-fembot-pink/20">
                  <Heart className="h-4 w-4" />
                  <span>Support Our Project</span>
                </div>
              </div>
             <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight bg-gradient-to-b from-foreground to-foreground/70 bg-clip-text text-transparent">
                Support the Team
              </h1>
              <p className="text-muted-foreground text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
                Help us make Discord better for everyone. Your support enables us to create amazing features and maintain high-quality service.
              </p>
            </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center p-6 rounded-lg bg-card border">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Rocket className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Faster Development</h3>
              <p className="text-sm text-muted-foreground">Enable us to implement new features and improvements more quickly</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 rounded-lg bg-card border">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Community Growth</h3>
              <p className="text-sm text-muted-foreground">Help us expand and support our growing community better</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 rounded-lg bg-card border">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Star className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Features</h3>
              <p className="text-sm text-muted-foreground">Support the development of advanced features for all users</p>
            </div>
          </div>
        </div>

        {/* Support Options */}
        <div className="container mx-auto px-4 py-12">
          <Tabs defaultValue="kofi" className="max-w-3xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="kofi" className="flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                <span>One-time Support</span>
              </TabsTrigger>
              <TabsTrigger value="giftcard" className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                <span>Gift Card Support</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="kofi">
                      <Card className="border-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl flex items-center gap-2">
                      <Coffee className="h-6 w-6 text-fembot-pink" />
                      Support via Ko-fi
                    </CardTitle>
                    <CardDescription className="text-lg mt-2">
                      Quick and easy way to support our development
                    </CardDescription>
                  </div>
                  <div className="bg-fembot-pink/10 p-3 rounded-full">
                    <Heart className="h-6 w-6 text-fembot-pink" />
                  </div>
                </div>
              </CardHeader>
                <CardContent className="relative space-y-6">
                  <div className="space-y-4">
                    <div className="bg-primary/5 rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        Why Support Us?
                      </h3>
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <span className="mt-1">💫</span>
                          <span>Be part of making Discord servers more engaging and fun</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">🌟</span>
                          <span>Help shape the future of community management</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">💝</span>
                          <span>Support indie developers who care about the community</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">✨</span>
                          <span>Feel good knowing you're helping Discord users</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Button
                      size="lg"
                      onClick={() => window.open("https://ko-fi.com/JanIsHere", "_blank")}
                      className="bg-[#29abe0] hover:bg-[#29abe0]/90 text-white px-8 py-6 text-lg relative overflow-hidden group"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        <Coffee className="h-5 w-5" />
                        Support on Ko-fi
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"></div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="giftcard">
              <div className="space-y-8">
                <Card className="border-2">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-2xl flex items-center gap-2">
                          <Gift className="h-6 w-6 text-fembot-pink" />
                          Gift Card Support
                        </CardTitle>
                        <CardDescription className="text-lg mt-2">
                          Support us with German gift cards
                        </CardDescription>
                      </div>
                      <div className="bg-fembot-pink/10 p-3 rounded-full">
                        <Heart className="h-6 w-6 text-fembot-pink" />
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    
                  {/* Why Support Section */}
                    <div className="bg-primary/5 rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        Why Support Us?
                      </h3>
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <span className="mt-1">💫</span>
                          <span>Be part of making Discord servers more engaging and fun</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">🌟</span>
                          <span>Help shape the future of community management</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">💝</span>
                          <span>Support indie developers who care about the community</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1">✨</span>
                          <span>Feel good knowing you're helping thousands of Discord users</span>
                        </li>
                      </ul>
                    </div>
                    
                    {/* Gift Card Types Overview */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {giftCards.map((card) => (
                        <div 
                          key={card.value} 
                          className={`p-3 rounded-lg border transition-all cursor-pointer
                            ${giftCardType === card.value 
                              ? 'border-fembot-pink bg-fembot-pink/5' 
                              : 'border-primary/10 hover:border-fembot-pink/30'}`}
                          onClick={() => {
                            setGiftCardType(card.value)
                            if (giftCardCode) {
                              const formattedCode = formatGiftCardCode(giftCardCode, card.value)
                              setGiftCardCode(formattedCode)
                              validateGiftCard(formattedCode, card.value)
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Gift className="h-4 w-4 text-fembot-pink" />
                            <span className="font-medium text-sm">{card.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{card.description}</p>
                        </div>
                      ))}
                    </div>

                    {/* Gift Card Form */}
                    <div className="bg-card border rounded-lg p-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="code" className="text-sm font-medium">Gift Card Code</Label>
                          <div className="space-y-1">
                            <Input
                              id="code"
                              placeholder={giftCardType ? codePatterns[giftCardType as keyof typeof codePatterns]?.example : "Select a gift card type above first"}
                              value={giftCardCode}
                              onChange={handleGiftCardCodeChange}
                              className={`w-full ${codeError ? 'border-red-500 focus:ring-red-500' : ''}`}
                            />
                            {codeError && (
                              <p className="text-sm text-red-500">{codeError}</p>
                            )}
                            {giftCardType && !codeError && (
                              <p className="text-xs text-muted-foreground">
                                Format: {codePatterns[giftCardType as keyof typeof codePatterns]?.description}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-sm font-medium">Personal Message (Optional)</Label>
                          <Textarea
                            id="message"
                            placeholder="Want to share why you're supporting us? Add a message here..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="min-h-[100px] w-full"
                          />
                        </div>

                        <Button 
                          className="w-full bg-fembot-pink hover:bg-fembot-pink/90"
                          onClick={handleSubmitGiftCard}
                          disabled={!giftCardType || !giftCardCode || Boolean(codeError)}
                        >
                          <Send className="mr-2 h-4 w-4" /> Submit Gift Card
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Final CTA */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto bg-card border rounded-lg p-8 text-center space-y-4">
            <h3 className="text-2xl font-semibold">Join Our Community</h3>
            <p className="text-muted-foreground">
              Be part of something bigger! Join our Discord community to connect with other supporters and the development team.
            </p>
            <Button 
              className="mt-4"
              variant="outline"
              onClick={() => window.open('https://discord.gg/YjtsNfm2Uk', '_blank')}
            >
              Join Discord Server
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Donate
